import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import isBefore from 'date-fns/isBefore';
import { Spacer } from '../Spacer';
import { Input } from '../forms/Input';
import { Typography } from '../Typography';
import { isValidDate } from '../../utils/isValidDate';
import { Panel, PanelHeadingMarginSizes } from '../Panel';
import { Checkbox } from '../forms/Checkbox';
import { StampDutyCalculatorResult, UseStampDutyCalculator } from '../../hooks/useStampDutyCalculator';
import { getTodaysDate } from '../../utils/getTodaysDate';
import { currency } from '../../utils';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { FinancialServicesForm } from '../forms/FinancialServicesForm';
import { UiStateContext } from '../../context/UiStateContext';
import { Button } from '../Button';

// Interfaces

type Views = 'calculator' | 'results';

export interface StampDutyCalculatorProps {
  purchasePrice?: number;
  border?: boolean;
}

// CONST
interface PanelConfig {
  heading: string;
  headingMargin: PanelHeadingMarginSizes;
  backgroundColor: ThemeColorsKeys;
}
const PANEL_CONFIG: PanelConfig = {
  heading: 'Stamp Duty calculator',
  headingMargin: 'double',
  backgroundColor: 'white',
};

// JSX

interface StampDutyCalculatorFormData {
  price: number;
  nonUkResident: boolean;
  effectiveDate: string;
}

const Result: FC<ResultProps> = ({ activePurchasePrice, results, firstTimeBuyer }) => (
  <>
    {results && (
      <Spacer as="dl" spacing="quarter">
        <div>
          <Typography component="dt" variant="titleSmall">
            Purchase price
          </Typography>
          <Typography component="dd" variant="bodyLarge">
            {currency(activePurchasePrice)}
          </Typography>
        </div>
        <div>
          <Typography component="dt" variant="titleSmall">
            Total due (Main residence)
          </Typography>
          <Typography component="dd" variant="bodyLarge">
            {results.totalDue === 0 ? '£0' : currency(results.totalDue)}
          </Typography>
        </div>
        {!firstTimeBuyer && (
          <div>
            <Typography component="dt" variant="titleSmall">
              Total due (Additional property)
            </Typography>
            <Typography component="dd" variant="bodyLarge">
              {currency(results.totalDueAdditional)}
            </Typography>
          </div>
        )}
      </Spacer>
    )}
  </>
);

export const StampDutyCalculator: FC<StampDutyCalculatorProps> = ({ purchasePrice, border, ...rest }) => {
  const { stampDutyCalculatorResults, handleStampDutyCalculation, loading } = UseStampDutyCalculator();
  const [activePurchasePrice, setActivePurchasePrice] = useState<number>();
  const [showFirstTimeBuyerResults, setShowFirstTimeBuyerResults] = useState(false);
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false);
  const [nonResident, setNonResident] = useState(false);
  const [firstTimeResults, setFirstTimeResults] = useState();
  const [activeView, setActiveView] = useState<Views>('calculator');
  const [results, setResults] = useState<StampDutyCalculatorResult>();
  const [effectiveDate, setEffectiveDate] = useState<string>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [beforeMiniBudget, setBeforeMiniBudget] = useState<boolean>(false);
  const { dataLayerPush } = useDataLayerPush();
  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const formBladeTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;

  const formMethods = useForm<StampDutyCalculatorFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstTimeBuyer: false,
      nonUkResident: false,
      effectiveDate: getTodaysDate(),
      price: purchasePrice,
    },
  });

  const { register, handleSubmit, control, formState, reset } = formMethods;
  const { errors, dirtyFields, touchedFields } = formState;

  // Handle data change

  useEffect(() => {
    if (stampDutyCalculatorResults && stampDutyCalculatorResults.stampDuty.stampDutyCalculator.results.length > 0) {
      const activeResults = stampDutyCalculatorResults.stampDuty.stampDutyCalculator;
      setNoResults(false);
      setResults(activeResults.results[0]);
      setFirstTimeResults(activeResults.results[1]);
      setActivePurchasePrice(activeResults.price);
      setActiveView('results');
    } else if (
      stampDutyCalculatorResults &&
      stampDutyCalculatorResults.stampDuty.stampDutyCalculator.results.length === 0
    ) {
      setActiveView('results');
      setNoResults(true);
    }
  }, [stampDutyCalculatorResults]);

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit = (formData: StampDutyCalculatorFormData) => {
    const stringToDate = (_date, _format, _delimiter) => {
      const formatLowerCase = _format.toLowerCase();
      const formatItems = formatLowerCase.split(_delimiter);
      const dateItems = _date.split(_delimiter);
      const monthIndex = formatItems.indexOf('mm');
      const dayIndex = formatItems.indexOf('dd');
      const yearIndex = formatItems.indexOf('yyyy');
      let month = parseInt(dateItems[monthIndex]);
      month -= 1;
      const formatedDate = new Date(dateItems[yearIndex], month, dateItems[dayIndex]);
      return formatedDate;
    };

    const date = stringToDate(formData.effectiveDate, 'dd/MM/yyyy', '/');
    const miniBudget = stringToDate('31/03/2025', 'dd/MM/yyyy', '/');

    setBeforeMiniBudget(isBefore(date, miniBudget));

    // Date format
    const dateParts = formData.effectiveDate.split('/');
    const effectiveDateString = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    // Price format
    const { price } = formData;

    // Run calculation

    handleStampDutyCalculation(effectiveDateString, price, formData.nonUkResident);

    // Set the date for use in results
    setEffectiveDate(formData.effectiveDate);

    // Set first time buyer for results

    setFirstTimeBuyer(!!formData.firstTimeBuyer);

    if (formData.firstTimeBuyer && !formData.nonUkResident) {
      setShowFirstTimeBuyerResults(true);
    } else {
      setShowFirstTimeBuyerResults(false);
    }

    if (formData.nonUkResident) {
      setNonResident(true);
    } else {
      setNonResident(false);
    }

    // Check if we already have results

    if (results || firstTimeResults) {
      setNoResults(false);
      setActiveView('results');
    }

    dataLayerPush({
      event: 'stampDutyCalculator',
      sdcFormStep: 'Calculate Stamp Duty',
      sdcPurchasePrice: price,
      sdcDateOfPurchase: formData.effectiveDate,
      sdcUKResident: !formData.nonUkResident,
      formValidation: 'success',
      formError: undefined,
    });
  };

  // Function to handle the get in touch click
  const handleGetInTouchClick = () => {
    setActiveBlade('financialServices');
    setActiveBladeTriggerEl(formBladeTriggerEl);
  };

  return (
    <FormProvider {...formMethods}>
      {/* Calculator view */}
      {activeView === 'calculator' && (
        <Panel
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          method="post"
          noValidate
          buttonOne={{
            label: 'Calculate Stamp Duty',
            variant: 'outline',
            type: 'submit',
            disableClickTracking: true,
          }}
          {...PANEL_CONFIG}
          {...rest}
          border={border}
        >
          <Spacer spacing="half">
            <Input
              type="number"
              invalid={!!errors.price}
              valid={touchedFields.price && dirtyFields.price && !errors.price}
              label="Purchase price"
              id="price"
              placeholder="e.g 450000"
              {...register('price', { valueAsNumber: true, required: true, min: 1 })}
              required
              prefix="£"
              hideAsterisk
            />
            <Input
              type="text"
              invalid={!!errors.effectiveDate}
              valid={touchedFields.effectiveDate && dirtyFields.effectiveDate && !errors.effectiveDate}
              label="Effective date of purchase"
              id="effectiveDate"
              placeholder="dd/mm/yyyy"
              {...register('effectiveDate', {
                required: true,
                validate: {
                  isValidFormat: (v) => {
                    const regex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
                    return regex.test(v) || 'Please enter a valid date (dd/mm/yyyy)';
                  },
                  isValidDate: (v) => isValidDate(v) || 'Please enter a valid date (dd/mm/yyyy)',
                },
              })}
              required
              errorMessage={errors.effectiveDate?.message}
              hideAsterisk
            />

            <Controller
              name="firstTimeBuyer"
              control={control}
              render={({ field }) => <Checkbox label="I am a first-time buyer" id="firstTimeBuyer" {...field} />}
            />
            <Controller
              name="nonUkResident"
              control={control}
              render={({ field }) => <Checkbox label="I am a non-UK resident" id="nonUkResident" {...field} />}
            />
          </Spacer>
        </Panel>
      )}
      {/* Results view */}
      {activeView === 'results' && results && !noResults && (
        <Panel
          buttonOne={{
            label: 'Calculate again',
            variant: 'outline',
            type: 'button',
            disableClickTracking: true,
            onClick: () => {
              setActiveView('calculator');
              dataLayerPush({
                event: 'stampDutyCalculator',
                sdcFormStep: 'Calculate again',
                sdcPurchasePrice: activePurchasePrice,
                sdcDateOfPurchase: effectiveDate,
                sdcUKResident: nonResident,
                formValidation: 'success',
                formError: undefined,
              });
              reset();
            },
          }}
          {...PANEL_CONFIG}
          {...rest}
          border={border}
        >
          <Spacer spacing="half">
            <Result
              activePurchasePrice={activePurchasePrice}
              firstTimeBuyer={firstTimeBuyer}
              results={showFirstTimeBuyerResults && firstTimeResults ? firstTimeResults : results}
            />
            <Spacer spacing="quarter">
              <Typography component="p" variant="bodySmall">
                First-time buyers will not pay any stamp duty on the first £300,000 and will pay a reduced rate of 5% on
                the portion from £300,001 to £500,000.
              </Typography>
              <Typography component="p" variant="bodySmall">
                Speak to our expert mortgage consultants to find out how much Stamp Duty Land Tax you will have to pay
                when buying a property in the UK.
              </Typography>
              <Button variant="link" type="button" onClick={() => handleGetInTouchClick()} ref={formBladeTriggerEl}>
                Get in touch
              </Button>
            </Spacer>
          </Spacer>
        </Panel>
      )}
      <FinancialServicesForm />
    </FormProvider>
  );
};
